import type { Editor } from '@tiptap/core'
import { NodeSelection, type Selection } from 'prosemirror-state'
import type { EditorView } from 'prosemirror-view'

/** How long the bubble menu waits after Tab selected a cell's content, so a run of Tab presses is not met by it */
export const TAB_SELECTION_MENU_DELAY_MS = 700

type Pending = { from: number; to: number; timer: ReturnType<typeof setTimeout> }

/**
 * Selections an editor was given by Tab (a table cell selects its content when Tab reaches it). While such a selection
 * is fresh the bubble menu is held back; once the delay passes, or the selection changes, it shows as for any other.
 * Keyed by editor, because the pooled editor is shared by many cells.
 */
const pending = new WeakMap<Editor, Pending>()

/**
 * BubbleMenu skips an update when neither the selection nor the document changed, so a held-back menu is released
 * through the focus handler it also listens to. ProseMirror's own focus handling stamps `lastFocus` again; reset it.
 */
function nudgeBubbleMenu(editor: Editor): void {
  const view = editor.view
  if (!view.hasFocus()) return
  view.dom.dispatchEvent(new Event('focus'))
  keepSelectionAfterFocus(view)
}

/**
 * For 200 ms after a focus, ProseMirror takes a collapsed selection at the document start for the browser resetting it
 * and restores its own. A selection placed on purpose (Tab selecting a cell, then Home) must not be undone by that.
 */
export function keepSelectionAfterFocus(view: EditorView): void {
  const input = (view as unknown as { input?: { lastFocus?: number } }).input
  if (input && typeof input.lastFocus === 'number') input.lastFocus = 0
}

export function markTabSelection(editor: Editor, selection: Selection): void {
  const previous = pending.get(editor)
  if (previous) clearTimeout(previous.timer)
  if (selection.empty) {
    pending.delete(editor)
    return
  }
  const { from, to } = selection
  const timer = setTimeout(() => {
    const current = pending.get(editor)
    if (!current || current.timer !== timer) return
    pending.delete(editor)
    // The menu re-evaluates on a state update: nudge it if the selection is still the one Tab made
    if (editor.isDestroyed) return
    const now = editor.state.selection
    if (now.from === from && now.to === to) nudgeBubbleMenu(editor)
  }, TAB_SELECTION_MENU_DELAY_MS)
  pending.set(editor, { from, to, timer })
}

export function isTabSelection(editor: Editor, selection: { from: number; to: number }): boolean {
  const marked = pending.get(editor)
  return !!marked && marked.from === selection.from && marked.to === selection.to
}

/** Selections whose bubble menu the user closed with Escape. It stays closed until the selection changes. */
const dismissed = new WeakMap<Editor, { from: number; to: number }>()

export function dismissBubbleMenu(editor: Editor): void {
  const { from, to } = editor.state.selection
  dismissed.set(editor, { from, to })
  // Re-evaluates the menu now, which hides it
  nudgeBubbleMenu(editor)
}

export function isBubbleMenuDismissed(editor: Editor, selection: { from: number; to: number }): boolean {
  const marked = dismissed.get(editor)
  return !!marked && marked.from === selection.from && marked.to === selection.to
}

/** An atom node (bookmark card, embed, divider) selected as a whole: the text toolbar has nothing to format in it */
export function isNodeSelection(selection: object): boolean {
  return selection instanceof NodeSelection
}
